import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe2, ChevronRight, ShieldCheck, Award } from "lucide-react";
import api from "../lib/api";
import STATIC_COURSES from "../lib/coursesData";

export default function Courses() {
  const [courses, setCourses] = useState(STATIC_COURSES);
  const [filtered, setFiltered] = useState(STATIC_COURSES);
  const [category, setCategory] = useState("All");
  const navigate = useNavigate();

  const CATEGORIES = [
    "All", "Language Courses", "Academic Courses", "Skill Development"
  ];

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/courses");
        if (res.data && res.data.length > 0) {
          setCourses(res.data);
          setFiltered(res.data);
        }
      } catch (err) {
        console.error("Failed to load courses from API, using static data", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (category === "All") {
      setFiltered(courses);
    } else {
      setFiltered(courses.filter(c => c.category === category));
    }
  }, [category, courses]);

  return (
    <main className="pt-32 pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-extrabold text-gray-900 uppercase tracking-tight">
            Explore Our <span className="text-[#FFC107]">Courses</span>
          </h1>
          <p className="mt-4 text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed font-medium">
            Find the right courses for your future. Whether it's mastering a new language or developing essential softskills, we have you covered.
          </p>
        </div>

        {/* Categories Filter (Matching Screenshot Chips) */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-8 py-2.5 rounded-full text-xs font-black transition-all border-2 ${
                category === cat 
                  ? "bg-[#FFC107] border-[#FFC107] text-white shadow-lg" 
                  : "bg-white border-gray-100 text-gray-600 hover:border-[#FFC107] hover:text-[#FFC107]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {filtered.map((c) => (
            <div 
              key={c.course_id}
              className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-300 group cursor-pointer"
              onClick={() => navigate(`/courses/${c.slug}`)}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={c.image_url} 
                  alt={c.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#FFC107] text-gray-900 text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-md shadow-lg">
                    {c.category}
                  </span>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-black text-gray-900 mb-6 h-14 line-clamp-2 leading-snug">
                  {c.title}
                </h3>
                
                <p className="text-gray-500 text-sm font-medium mb-6 line-clamp-2 h-10 leading-relaxed">
                  {c.short_description || "Professional training and development program."}
                </p>

                <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-50 pb-6">
                  <span className="bg-blue-50 text-[#0056b3] text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-md border border-blue-100/50 flex items-center gap-1.5">
                    <Globe2 className="w-3.5 h-3.5 text-[#0056b3]" /> Online
                  </span>
                  <span className="bg-amber-50 text-amber-700 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-md border border-amber-100/50 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-amber-600" /> Verified
                  </span>
                  <span className="bg-purple-50 text-purple-700 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-md border border-purple-100/50 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5 text-purple-600" /> Top Rated
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    {c.contact_for_pricing ? (
                      <span className="text-lg font-black text-[#0056b3]">Contact for Pricing</span>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black text-[#0056b3]">₹{c.price}</span>
                        <span className="text-gray-400 text-[10px] font-bold">onwards</span>
                      </div>
                    )}
                  </div>
                  <button className="flex items-center gap-2 text-[11px] font-black text-[#FFC107] border-2 border-[#FFC107] px-6 py-2.5 rounded-lg hover:bg-[#FFC107] hover:text-white transition-all">
                    Enroll Now <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
